// components/ResumeModal.jsx
import React, { useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";

const backdropVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.2 } },
  exit: { opacity: 0, transition: { duration: 0.2 } },
};

const modalVariants = {
  hidden: { opacity: 0, scale: 0.95 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.3 } },
  exit: { opacity: 0, scale: 0.95, transition: { duration: 0.2 } },
};

const ResumeModal = ({ show, onClose, resumeOptions = [], selectedResume = {}, onSelectResume }) => {
  const [zoom, setZoom] = useState(1.0);
  const iframeRef = useRef(null);

  const handleZoomIn = () => {
    setZoom((prev) => Math.min(prev + 0.1, 2));
  };

  const handleZoomOut = () => {
    setZoom((prev) => Math.max(prev - 0.1, 0.5));
  };

  const handlePrint = () => {
    if (iframeRef.current) {
      iframeRef.current.contentWindow.focus();
      iframeRef.current.contentWindow.print();
    }
  };

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center backdrop-blur-md bg-black/50 px-2 py-8"
          variants={backdropVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          onClick={onClose}
        >
          <motion.div
            className="relative w-full max-w-7xl h-[95vh] bg-white rounded-xl shadow-xl flex flex-col overflow-hidden border"
            variants={modalVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Toolbar Header */}
            <div className="flex flex-col md:flex-row justify-between items-center px-4 py-2.5 border-b bg-gray-100 gap-2">
              <div className="flex flex-wrap items-center justify-center gap-2">
                <span className="text-xs font-bold text-gray-700 hidden sm:inline">Version:</span>
                {resumeOptions.map((opt) => (
                  <button
                    key={opt.name}
                    onClick={() => onSelectResume(opt)}
                    className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-all cursor-pointer flex items-center gap-1.5 ${
                      selectedResume.name === opt.name
                        ? "bg-slate-900 text-white shadow-md scale-[1.02]"
                        : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                    }`}
                  >
                    {opt.icon}
                    <span>{opt.name.replace("Resume (", "").replace(")", "").replace("Latest Resume (Live)", "Live Update")}</span>
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2 sm:gap-3">
                <button
                  onClick={handleZoomOut}
                  className="px-2 py-1 bg-yellow-300 rounded hover:bg-yellow-400 text-xs font-semibold text-gray-800 transition-colors"
                >
                  − Zoom
                </button>
                <button
                  onClick={handleZoomIn}
                  className="px-2 py-1 bg-yellow-300 rounded hover:bg-yellow-400 text-xs font-semibold text-gray-800 transition-colors"
                >
                  + Zoom
                </button>
              
                <button
                  onClick={handlePrint}
                  className="px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700 text-xs font-semibold transition-colors"
                >
                  Print
                </button>
                <button
                  onClick={onClose}
                  className="text-gray-600 hover:text-red-500 text-2xl font-bold leading-none ml-2"
                >
                  ×
                </button>
              </div>
            </div>

            {/* Iframe Viewer */}
            <div className="flex-1 bg-gray-200 overflow-auto">
              <iframe
                ref={iframeRef}
                src={selectedResume?.url || ""}
                title="Resume Viewer"
                className="w-full h-full"
                style={{
                  transform: `scale(${zoom})`,
                  transformOrigin: "top center",
                }}
              />
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ResumeModal;
